import { Loader2 } from 'lucide-react';
import { FcGoogle } from 'react-icons/fc';
import { EmailPasswordForm } from '@/auth/EmailPasswordForm';

interface InviteSignInProps {
  partnerName?: string;
  /** The address the invite is locked to — the account must be created for it. */
  invitedEmail: string;
  busy: boolean;
  error: string | null;
  onGoogle: () => void;
  onError: (message: string | null) => void;
}

/**
 * Creating the account behind a *staff* invitation: Google, or email and
 * password. The whole of what a staff member is asked — their partner has already
 * registered, so there is nothing else to collect.
 *
 * A manager's credentials step is `OnboardingStepAccount` instead, not this.
 * They are deliberately not one component: this one has to cope with an account
 * the visitor may already have, and offers the choice openly, whereas a manager
 * is at an address Eighty Days has only just invited and should never be asked
 * to consider it.
 */
export function InviteSignIn({
  partnerName,
  invitedEmail,
  busy,
  error,
  onGoogle,
  onError,
}: InviteSignInProps) {
  return (
    <div className='tw:mx-auto tw:w-full tw:max-w-sm tw:text-center'>
      <p className='tw:text-white'>
        <span className='tw:font-serif tw:text-xl tw:text-ed-gold'>{partnerName}</span>
        <br />
        has been invited to Eighty Days Experiences.
      </p>
      <p className='tw:mt-3 tw:mb-8 tw:text-sm tw:text-white/60'>
        Sign in as <span className='tw:text-white'>{invitedEmail}</span> to create your account.
      </p>

      <button
        type='button'
        onClick={onGoogle}
        disabled={busy}
        aria-busy={busy || undefined}
        className='tw:inline-flex tw:w-full tw:items-center tw:justify-center tw:gap-3 tw:rounded tw:bg-white tw:px-6 tw:py-3 tw:text-sm tw:font-medium tw:text-ed-black tw:transition-opacity tw:hover:opacity-90 tw:disabled:opacity-60'
      >
        {busy ? <Loader2 className='tw:size-5 tw:animate-spin' /> : <FcGoogle className='tw:size-5' />}
        {busy ? 'Signing in…' : 'Continue with Google'}
      </button>

      <p className='tw:my-6 tw:text-xs tw:text-white/50'>or</p>

      <EmailPasswordForm mode='create-account' fixedEmail={invitedEmail} onError={onError} disabled={busy} />

      {error && <p className='tw:mt-4 tw:text-sm tw:text-ed-red'>{error}</p>}
    </div>
  );
}
